import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { decisionAPI, invernadaAPI } from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, ScatterChart, Scatter, Legend,
} from 'recharts';

const CLASIF_COLORS = { top: '#16a34a', medio: '#f59e0b', descarte: '#ef4444', alto: '#16a34a', bajo: '#ef4444' };
const CLASIF_LABELS = { top: 'TOP', medio: 'MEDIO', descarte: 'DESCARTE', alto: 'ALTO', bajo: 'BAJO' };
const PRIO_CLS = { alta: 'bg-red-100 text-red-800 border-red-200', media: 'bg-amber-100 text-amber-800 border-amber-200', baja: 'bg-blue-100 text-blue-800 border-blue-200' };

const Kpi = ({ label, value, sub, accent }) => (
  <div className={`bg-white rounded-xl border-l-4 ${accent || 'border-gray-300'} shadow-sm p-4 hover:shadow-md transition-shadow`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
    <p className="mt-1 text-xl font-bold text-gray-900">{value}</p>
    {sub && <p className="mt-0.5 text-[11px] text-gray-400">{sub}</p>}
  </div>
);

const Badge = ({ clasificacion }) => {
  const c = clasificacion || 'medio';
  const cls = { top: 'bg-green-100 text-green-800', medio: 'bg-amber-100 text-amber-800', descarte: 'bg-red-100 text-red-800', alto: 'bg-green-100 text-green-800', bajo: 'bg-red-100 text-red-800' };
  return <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${cls[c] || 'bg-gray-100 text-gray-700'}`}>{CLASIF_LABELS[c] || c}</span>;
};

const CountBox = ({ count, label, color }) => (
  <div className={`${color} rounded-xl p-3 text-center border`}>
    <p className="text-2xl font-bold">{count}</p>
    <p className="text-xs font-medium">{label}</p>
  </div>
);

const Spinner = ({ text }) => (
  <div className="flex items-center justify-center py-16">
    <div className="text-center">
      <div className="inline-block w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
      <p className="mt-2 text-sm text-gray-500">{text || 'Cargando...'}</p>
    </div>
  </div>
);

// ─── TABS ──────────────────────────────────────────────

const TABS = [
  { id: 'executive', label: 'Resumen Ejecutivo', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { id: 'top-cows', label: 'Top Vacas', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
  { id: 'reproductive', label: 'Reproduccion', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
  { id: 'feedlot', label: 'Invernada', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { id: 'bulls', label: 'Fertilidad Toros', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
  { id: 'alerts', label: 'Alertas', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z' },
];

const DecisionSupportPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('executive');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const estId = user?.establecimiento_id;

  const loadTab = useCallback(async (tab) => {
    if (!estId || data[tab]) return;
    setLoading(prev => ({ ...prev, [tab]: true }));
    try {
      let res;
      switch (tab) {
        case 'executive': res = await decisionAPI.executive(estId); break;
        case 'top-cows': res = await decisionAPI.topCows(estId); break;
        case 'reproductive': res = await decisionAPI.reproductive(estId); break;
        case 'feedlot': res = await invernadaAPI.getDashboard(estId); break;
        case 'bulls': res = await decisionAPI.bullFertility(estId); break;
        case 'alerts': res = await decisionAPI.alerts(estId); break;
        default: return;
      }
      setData(prev => ({ ...prev, [tab]: res.data }));
    } catch (err) { console.error(`Error loading ${tab}:`, err); }
    finally { setLoading(prev => ({ ...prev, [tab]: false })); }
  }, [estId, data]);

  useEffect(() => { loadTab(activeTab); }, [activeTab, loadTab]);

  const refresh = () => {
    setData(prev => { const copy = { ...prev }; delete copy[activeTab]; return copy; });
    setTimeout(() => loadTab(activeTab), 100);
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5" data-testid="decision-support-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Centro de Decisiones</h1>
          <p className="text-sm text-gray-500">Plataforma integrada de soporte para decisiones ganaderas</p>
        </div>
        <button onClick={refresh} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition self-start" data-testid="refresh-btn">
          Actualizar
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide" data-testid="decision-tabs">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition ${activeTab === t.id ? 'bg-green-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={t.icon} /></svg>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading[activeTab] && <Spinner text={`Cargando ${TABS.find(t => t.id === activeTab)?.label}...`} />}
      {!loading[activeTab] && data[activeTab] && (
        <>
          {activeTab === 'executive' && <ExecutiveTab data={data.executive} />}
          {activeTab === 'top-cows' && <TopCowsTab data={data['top-cows']} />}
          {activeTab === 'reproductive' && <ReproductiveTab data={data.reproductive} />}
          {activeTab === 'feedlot' && <FeedlotTab data={data.feedlot} />}
          {activeTab === 'bulls' && <BullsTab data={data.bulls} />}
          {activeTab === 'alerts' && <AlertsTab data={data.alerts} />}
        </>
      )}
    </div>
  );
};

// ─── 1. EXECUTIVE TAB ──────────────────────────────────

const ExecutiveTab = ({ data }) => {
  const { kpis: k, top_10_vacas } = data;
  const pieData = [
    { name: 'Preñadas', value: Math.round(k.tasa_prenez), fill: '#16a34a' },
    { name: 'Vacías', value: Math.round(100 - k.tasa_prenez), fill: '#ef4444' },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <Kpi label="Vacas Activas" value={k.vacas_activas} accent="border-pink-500" sub={`${k.toros_activos} toros`} />
        <Kpi label="Total Terneros" value={k.total_terneros} accent="border-green-500" />
        <Kpi label="Kg Destete Total" value={`${k.total_kg_destete} kg`} accent="border-blue-500" />
        <Kpi label="Kg/Vaca/Año" value={`${k.kg_por_vaca_anio} kg`} accent="border-emerald-500" />
        <Kpi label="Tasa Preñez" value={`${k.tasa_prenez}%`} accent="border-purple-500" sub={`IEP: ${k.iep_promedio}d`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi label="Tasa Destete" value={`${k.tasa_destete}%`} accent="border-teal-500" />
        <Kpi label="IEP Promedio" value={`${k.iep_promedio} dias`} accent="border-orange-500" sub="Objetivo: < 365 dias" />
        <Kpi label="Dias Abiertos" value={`${k.dias_abiertos_promedio} dias`} accent="border-red-500" sub="Objetivo: < 90 dias" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top 10 Cows */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800">Top 10 Vacas por Productividad</h3>
          </div>
          <table className="w-full text-sm" data-testid="top-10-table">
            <thead className="bg-gray-50 text-gray-500 text-[11px] uppercase">
              <tr><th className="px-3 py-2 text-left">VID</th><th className="px-3 py-2">Raza</th><th className="px-3 py-2 text-right">Terneros</th><th className="px-3 py-2 text-right">Kg Destete</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {top_10_vacas.map((v, i) => (
                <tr key={v.animal_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-semibold">{v.VID}</td>
                  <td className="px-3 py-2 text-gray-500 text-center">{v.raza}</td>
                  <td className="px-3 py-2 text-right">{v.total_terneros}</td>
                  <td className="px-3 py-2 text-right font-bold">{v.kg_destete_total} kg</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pregnancy rate pie */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Tasa de Preñez</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}%`}>
                {pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ─── 2. TOP COWS TAB ───────────────────────────────────

const TopCowsTab = ({ data }) => {
  const { kpis: k, ranking } = data;
  const chartData = ranking.slice(0, 15).map(r => ({ VID: r.VID, kg: r.kg_destete_total, clasificacion: r.clasificacion }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total Vacas" value={k.total_vacas} accent="border-gray-500" />
        <Kpi label="Kg Destete Promedio" value={`${k.kg_destete_promedio} kg`} accent="border-blue-500" />
        <Kpi label="IEP Promedio" value={`${k.iep_promedio} dias`} accent="border-orange-500" />
        <div className="grid grid-cols-3 gap-2 col-span-2 md:col-span-1">
          <CountBox count={k.vacas_top} label="TOP" color="bg-green-50 text-green-700 border-green-200" />
          <CountBox count={k.vacas_medio} label="MEDIO" color="bg-amber-50 text-amber-700 border-amber-200" />
          <CountBox count={k.vacas_descarte} label="DESCARTE" color="bg-red-50 text-red-700 border-red-200" />
        </div>
      </div>

      {/* Bar chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Ranking Kg Destete (Top 15)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, bottom: 30, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="VID" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="kg" name="Kg Destete" radius={[3, 3, 0, 0]}>
                {chartData.map((e, i) => <Cell key={i} fill={CLASIF_COLORS[e.clasificacion] || '#6b7280'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100"><h3 className="text-sm font-semibold text-gray-800">Ranking Completo</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="top-cows-table">
            <thead className="bg-gray-50 text-gray-500 text-[11px] uppercase">
              <tr>
                <th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">VID</th><th className="px-3 py-2">Raza</th>
                <th className="px-3 py-2 text-right">Terneros</th><th className="px-3 py-2 text-right">Kg Destete</th>
                <th className="px-3 py-2 text-right">Kg/Año</th><th className="px-3 py-2 text-right">IEP</th>
                <th className="px-3 py-2 text-right">D.Abiertos</th><th className="px-3 py-2 text-center">Clasif.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {ranking.map(r => (
                <tr key={r.animal_id} className={`hover:bg-gray-50 ${r.clasificacion === 'descarte' ? 'bg-red-50/40' : ''}`}>
                  <td className="px-3 py-2 text-gray-400 font-mono text-xs">{r.ranking}</td>
                  <td className="px-3 py-2 font-semibold">{r.VID}</td>
                  <td className="px-3 py-2 text-center text-gray-500">{r.raza}</td>
                  <td className="px-3 py-2 text-right">{r.total_terneros}</td>
                  <td className="px-3 py-2 text-right font-bold">{r.kg_destete_total} kg</td>
                  <td className="px-3 py-2 text-right">{r.kg_por_anio_productivo}</td>
                  <td className="px-3 py-2 text-right">{r.iep || '-'}</td>
                  <td className="px-3 py-2 text-right">{r.dias_abiertos}</td>
                  <td className="px-3 py-2 text-center"><Badge clasificacion={r.clasificacion} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── 3. REPRODUCTIVE TAB ───────────────────────────────

const ReproductiveTab = ({ data }) => {
  const { kpis: k, detalle, alertas } = data;
  const pieData = [
    { name: 'Preñadas', value: k.vacas_prenadas, fill: '#16a34a' },
    { name: 'Vacías', value: k.vacas_vacias, fill: '#ef4444' },
    { name: 'Pendientes', value: k.vacas_pendientes, fill: '#f59e0b' },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Vacas Preñadas" value={k.vacas_prenadas} accent="border-green-500" />
        <Kpi label="Vacas Vacias" value={k.vacas_vacias} accent="border-red-500" />
        <Kpi label="Tasa Preñez" value={`${k.tasa_prenez}%`} accent="border-purple-500" />
        <Kpi label="IEP Promedio" value={`${k.iep_promedio} dias`} accent="border-orange-500" sub="Objetivo: < 365d" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Pie chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Estado Reproductivo</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                {pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Alerts */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" data-testid="repro-alertas">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Alertas Reproductivas ({alertas.length})</h3>
          {alertas.length === 0 && <p className="text-sm text-gray-400 py-4">Sin alertas reproductivas</p>}
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {alertas.map((a, i) => (
              <div key={i} className="p-2.5 rounded-lg bg-red-50 border border-red-100">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 bg-red-100 text-red-700 rounded">{a.tipo}</span>
                  <span className="text-xs font-semibold text-gray-800">{a.VID}</span>
                </div>
                <p className="text-xs text-gray-600">{a.mensaje}</p>
                <p className="text-[11px] text-green-700 font-medium mt-1">{a.accion}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detail table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100"><h3 className="text-sm font-semibold text-gray-800">Detalle Reproductivo ({detalle.length} vacas)</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="repro-table">
            <thead className="bg-gray-50 text-gray-500 text-[11px] uppercase">
              <tr>
                <th className="px-3 py-2 text-left">VID</th><th className="px-3 py-2">Raza</th>
                <th className="px-3 py-2 text-right">Servicios</th><th className="px-3 py-2 text-right">Preñeces</th>
                <th className="px-3 py-2 text-right">Vacias</th><th className="px-3 py-2 text-right">Partos</th>
                <th className="px-3 py-2 text-right">D.Abiertos</th><th className="px-3 py-2 text-center">Ultimo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {detalle.map(r => (
                <tr key={r.animal_id} className={`hover:bg-gray-50 ${r.ultimo_resultado === 'vacia' ? 'bg-red-50/30' : ''}`}>
                  <td className="px-3 py-2 font-semibold">{r.VID}</td>
                  <td className="px-3 py-2 text-center text-gray-500">{r.raza}</td>
                  <td className="px-3 py-2 text-right">{r.total_servicios}</td>
                  <td className="px-3 py-2 text-right text-green-700 font-medium">{r.preneces}</td>
                  <td className="px-3 py-2 text-right text-red-600 font-medium">{r.vacias}</td>
                  <td className="px-3 py-2 text-right">{r.total_partos}</td>
                  <td className="px-3 py-2 text-right">{r.dias_abiertos}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${r.ultimo_resultado === 'preñada' ? 'bg-green-100 text-green-800' : r.ultimo_resultado === 'vacia' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                      {r.ultimo_resultado}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── 4. FEEDLOT TAB ────────────────────────────────────

const FeedlotTab = ({ data }) => {
  const { kpis: k, tabla, alertas, chart_data } = data;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="ADG Promedio" value={`${k.adg_promedio} kg/d`} accent="border-blue-500" />
        <Kpi label="Costo/kg Ganado" value={`$${k.costo_por_kg_promedio}`} accent="border-orange-500" />
        <Kpi label="Margen Total" value={`$${(k.margen_total || 0).toLocaleString()}`} accent={k.margen_total >= 0 ? 'border-green-500' : 'border-red-500'} sub={`Revenue: $${(k.revenue_total || 0).toLocaleString()}`} />
        <Kpi label="Animales" value={k.total_animales} accent="border-gray-500" sub={`${k.total_lotes} lotes`} />
      </div>

      {alertas && alertas.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4" data-testid="feedlot-alertas">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Alertas ({alertas.length})</h3>
          <div className="space-y-1.5 max-h-36 overflow-y-auto">
            {alertas.map((a, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-100 rounded">
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded whitespace-nowrap">{a.tipo.replace('_', ' ')}</span>
                <p className="text-xs text-gray-700">{a.animal}: {a.mensaje}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {chart_data && chart_data.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">ADG vs Margen</h3>
          <ResponsiveContainer width="100%" height={260}>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="adg" name="ADG" unit=" kg/d" tick={{ fontSize: 10 }} />
              <YAxis dataKey="margen" name="Margen" unit=" $" tick={{ fontSize: 10 }} />
              <Tooltip />
              <Scatter data={chart_data}>
                {chart_data.map((e, i) => <Cell key={i} fill={CLASIF_COLORS[e.clasificacion] || '#6b7280'} r={5} />)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}

      {tabla && tabla.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="feedlot-table">
              <thead className="bg-gray-50 text-gray-500 text-[11px] uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">ID</th><th className="px-3 py-2 text-right">P.Ini</th>
                  <th className="px-3 py-2 text-right">P.Fin</th><th className="px-3 py-2 text-right">ADG</th>
                  <th className="px-3 py-2 text-right">Costo/kg</th><th className="px-3 py-2 text-right">Margen</th>
                  <th className="px-3 py-2 text-center">Clasif.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {tabla.map(a => (
                  <tr key={a.id} className={`hover:bg-gray-50 ${a.margen < 0 ? 'bg-red-50/30' : ''}`}>
                    <td className="px-3 py-2 font-medium">{a.identificacion || a.id?.slice(0, 8)}</td>
                    <td className="px-3 py-2 text-right">{a.peso_inicial} kg</td>
                    <td className="px-3 py-2 text-right">{a.peso_final} kg</td>
                    <td className="px-3 py-2 text-right font-semibold">{a.adg}</td>
                    <td className="px-3 py-2 text-right">${a.costo_por_kg}</td>
                    <td className={`px-3 py-2 text-right font-bold ${a.margen >= 0 ? 'text-green-700' : 'text-red-600'}`}>${a.margen}</td>
                    <td className="px-3 py-2 text-center"><Badge clasificacion={a.clasificacion} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {(!tabla || tabla.length === 0) && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400 text-sm">Sin datos de invernada. Registra animales en el modulo Invernada.</div>
      )}
    </div>
  );
};

// ─── 5. BULLS TAB ──────────────────────────────────────

const BullsTab = ({ data }) => {
  const { kpis: k, ranking } = data;
  const chartData = ranking.filter(r => r.vacas_expuestas > 0).map(r => ({ VID: r.VID, fertilidad: r.fertilidad_pct, clasificacion: r.clasificacion }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total Toros" value={k.total_toros} accent="border-blue-500" sub={`${k.total_con_datos_repro} con datos repro`} />
        <Kpi label="Fertilidad Promedio" value={`${k.fertilidad_promedio}%`} accent="border-green-500" />
        <div className="grid grid-cols-3 gap-2 col-span-2 md:col-span-2">
          <CountBox count={k.toros_top} label="TOP" color="bg-green-50 text-green-700 border-green-200" />
          <CountBox count={k.toros_medio} label="MEDIO" color="bg-amber-50 text-amber-700 border-amber-200" />
          <CountBox count={k.toros_descarte} label="DESCARTE" color="bg-red-50 text-red-700 border-red-200" />
        </div>
      </div>

      {chartData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Fertilidad por Toro (%)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, bottom: 30, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="VID" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" />
              <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} />
              <Tooltip />
              <Bar dataKey="fertilidad" name="Fertilidad %" radius={[3, 3, 0, 0]}>
                {chartData.map((e, i) => <Cell key={i} fill={CLASIF_COLORS[e.clasificacion] || '#6b7280'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bulls-table">
            <thead className="bg-gray-50 text-gray-500 text-[11px] uppercase">
              <tr>
                <th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">VID</th><th className="px-3 py-2">Raza</th>
                <th className="px-3 py-2 text-right">Expuestas</th><th className="px-3 py-2 text-right">Preñadas</th>
                <th className="px-3 py-2 text-right">Vacias</th><th className="px-3 py-2 text-right">Fertilidad</th>
                <th className="px-3 py-2 text-center">CE Rel.</th><th className="px-3 py-2 text-center">Clasif.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {ranking.map(r => (
                <tr key={r.animal_id} className={`hover:bg-gray-50 ${r.clasificacion === 'descarte' ? 'bg-red-50/30' : ''}`}>
                  <td className="px-3 py-2 text-gray-400 font-mono text-xs">{r.ranking}</td>
                  <td className="px-3 py-2 font-semibold">{r.VID}</td>
                  <td className="px-3 py-2 text-center text-gray-500">{r.raza}</td>
                  <td className="px-3 py-2 text-right">{r.vacas_expuestas}</td>
                  <td className="px-3 py-2 text-right text-green-700">{r.vacas_prenadas}</td>
                  <td className="px-3 py-2 text-right text-red-600">{r.vacas_vacias}</td>
                  <td className="px-3 py-2 text-right font-bold">{r.fertilidad_pct}%</td>
                  <td className="px-3 py-2 text-center">{r.ce_relativa ? r.ce_relativa.toFixed(2) : '-'}</td>
                  <td className="px-3 py-2 text-center"><Badge clasificacion={r.clasificacion} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {k.total_con_datos_repro === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
          No hay datos de servicios vinculados a toros. Para ver fertilidad por toro, registra servicios con el campo <strong>toro_id</strong>.
        </div>
      )}
    </div>
  );
};

// ─── 6. ALERTS TAB ─────────────────────────────────────

const AlertsTab = ({ data }) => {
  const { kpis: k, alertas, por_tipo } = data;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total Alertas" value={k.total_alertas} accent="border-gray-500" />
        <Kpi label="Alta Prioridad" value={k.alertas_alta} accent="border-red-500" />
        <Kpi label="Media Prioridad" value={k.alertas_media} accent="border-amber-500" />
        <Kpi label="Baja Prioridad" value={k.alertas_baja} accent="border-blue-500" />
      </div>

      {/* By type summary */}
      {por_tipo && Object.keys(por_tipo).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Alertas por Tipo</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(por_tipo).sort((a, b) => b[1] - a[1]).map(([tipo, count]) => (
              <span key={tipo} className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                {tipo.replace(/_/g, ' ')}: <strong>{count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Alert list */}
      <div className="space-y-2" data-testid="alerts-list">
        {alertas.map((a, i) => (
          <div key={i} className={`bg-white rounded-xl shadow-sm border p-4 ${a.prioridad === 'alta' ? 'border-red-200' : a.prioridad === 'media' ? 'border-amber-200' : 'border-gray-200'}`}>
            <div className="flex items-start gap-3">
              <span className={`px-2 py-0.5 text-[10px] font-bold rounded border whitespace-nowrap ${PRIO_CLS[a.prioridad] || PRIO_CLS.baja}`}>
                {(a.prioridad || 'baja').toUpperCase()}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-400">{a.tipo?.replace(/_/g, ' ')}</span>
                </div>
                <p className="text-sm text-gray-800 mt-0.5">{a.descripcion}</p>
                {a.accion_sugerida && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-100 rounded">
                    <p className="text-xs text-green-800 font-medium">Accion sugerida: {a.accion_sugerida}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {alertas.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400 text-sm">Sin alertas activas</div>
        )}
      </div>
    </div>
  );
};

export default DecisionSupportPage;
